import { useEffect, useRef } from "react";
import { Phone, PhoneOff, Mic, MicOff, Video, VideoOff } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { useProfile } from "@/hooks/useFriends";
import { mediaUrl } from "@/lib/api";
import { useCall } from "@/hooks/useCall";

function PeerAvatar({ peerUserId, size = "h-24 w-24" }: { peerUserId: string; size?: string }) {
  const { data: profile } = useProfile(peerUserId);
  return (
    <Avatar className={size}>
      <AvatarImage src={mediaUrl(profile?.avatar_url)} />
      <AvatarFallback className="text-3xl">{profile?.display_name?.[0] ?? "?"}</AvatarFallback>
    </Avatar>
  );
}

function PeerName({ peerUserId }: { peerUserId: string }) {
  const { data: profile } = useProfile(peerUserId);
  return <span>{profile?.display_name ?? "…"}</span>;
}

/**
 * Global call overlay, mounted once in AppLayout so an incoming call can
 * interrupt whatever screen the person is on - not scoped to ChatPage. Renders
 * nothing while status is "idle".
 */
export default function CallOverlay() {
  const { status, call, localStream, remoteStream, isMuted, isCameraOff, lastEndReason, acceptCall, declineCall, hangUp, toggleMute, toggleCamera } =
    useCall();

  const localVideoRef = useRef<HTMLVideoElement>(null);
  const remoteVideoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    if (localVideoRef.current) localVideoRef.current.srcObject = localStream;
  }, [localStream]);

  useEffect(() => {
    if (remoteVideoRef.current) remoteVideoRef.current.srcObject = remoteStream;
  }, [remoteStream]);

  // Fires once per call end - status has already gone back to "idle" by the
  // time this reason is set, so a toast is the only way left to tell the person
  // why (declined / unavailable / busy / failed), rather than the call screen
  // just silently vanishing.
  useEffect(() => {
    if (lastEndReason) toast(lastEndReason);
  }, [lastEndReason]);

  if (status === "idle" || !call) return null;

  if (status === "incoming-ringing") {
    return (
      <div className="fixed inset-0 z-50 flex flex-col items-center justify-between bg-background px-6 py-16">
        <div className="flex flex-1 flex-col items-center justify-center gap-4">
          <PeerAvatar peerUserId={call.peerUserId} />
          <div className="text-center">
            <div className="text-xl font-semibold">
              <PeerName peerUserId={call.peerUserId} />
            </div>
            <div className="text-muted-foreground">
              Incoming {call.withVideo ? "video" : "voice"} call…
            </div>
          </div>
        </div>
        <div className="flex items-center gap-10">
          <div className="flex flex-col items-center gap-2">
            <Button size="icon" variant="destructive" className="h-14 w-14 rounded-full" onClick={declineCall}>
              <PhoneOff className="h-6 w-6" />
            </Button>
            <span className="text-xs text-muted-foreground">Decline</span>
          </div>
          <div className="flex flex-col items-center gap-2">
            <Button size="icon" className="h-14 w-14 rounded-full bg-green-600 hover:bg-green-600/90" onClick={acceptCall}>
              <Phone className="h-6 w-6" />
            </Button>
            <span className="text-xs text-muted-foreground">Accept</span>
          </div>
        </div>
      </div>
    );
  }

  if (status === "outgoing-ringing" || status === "connecting") {
    return (
      <div className="fixed inset-0 z-50 flex flex-col items-center justify-between bg-background px-6 py-16">
        <div className="flex flex-1 flex-col items-center justify-center gap-4">
          <PeerAvatar peerUserId={call.peerUserId} />
          <div className="text-center">
            <div className="text-xl font-semibold">
              <PeerName peerUserId={call.peerUserId} />
            </div>
            <div className="text-muted-foreground">{status === "connecting" ? "Connecting…" : "Calling…"}</div>
          </div>
        </div>
        <div className="flex flex-col items-center gap-2">
          <Button size="icon" variant="destructive" className="h-14 w-14 rounded-full" onClick={hangUp}>
            <PhoneOff className="h-6 w-6" />
          </Button>
          <span className="text-xs text-muted-foreground">Cancel</span>
        </div>
      </div>
    );
  }

  // status === "active"
  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-black">
      {call.withVideo ? (
        <div className="relative flex-1">
          <video ref={remoteVideoRef} autoPlay playsInline className="h-full w-full object-cover" />
          <video
            ref={localVideoRef}
            autoPlay
            playsInline
            muted
            className="absolute right-4 top-4 h-32 w-24 rounded-lg border border-white/20 object-cover shadow-lg"
          />
        </div>
      ) : (
        <div className="flex flex-1 flex-col items-center justify-center gap-4 text-white">
          <PeerAvatar peerUserId={call.peerUserId} size="h-28 w-28" />
          <div className="text-xl font-semibold">
            <PeerName peerUserId={call.peerUserId} />
          </div>
          <div className="text-white/60">Voice call in progress</div>
          {/* Remote audio still needs a media element to actually play, even with
              no video track - it's just invisible. */}
          <video ref={remoteVideoRef} autoPlay playsInline className="hidden" />
        </div>
      )}

      <div className="flex items-center justify-center gap-6 bg-black/40 py-6">
        <div className="flex flex-col items-center gap-2">
          <Button
            size="icon"
            variant="secondary"
            className="h-12 w-12 rounded-full"
            onClick={toggleMute}
          >
            {isMuted ? <MicOff className="h-5 w-5" /> : <Mic className="h-5 w-5" />}
          </Button>
          <span className="text-xs text-white/70">{isMuted ? "Unmute" : "Mute"}</span>
        </div>
        {call.withVideo && (
          <div className="flex flex-col items-center gap-2">
            <Button
              size="icon"
              variant="secondary"
              className="h-12 w-12 rounded-full"
              onClick={toggleCamera}
            >
              {isCameraOff ? <VideoOff className="h-5 w-5" /> : <Video className="h-5 w-5" />}
            </Button>
            <span className="text-xs text-white/70">{isCameraOff ? "Start video" : "Stop video"}</span>
          </div>
        )}
        <div className="flex flex-col items-center gap-2">
          <Button size="icon" variant="destructive" className="h-12 w-12 rounded-full" onClick={hangUp}>
            <PhoneOff className="h-5 w-5" />
          </Button>
          <span className="text-xs text-white/70">End</span>
        </div>
      </div>
    </div>
  );
}
